// src/analytics/signup.ts
//
// The sign-up conversion and its errors (#937, #825).
//
// Signup used to fire from the email form's handler only. Google and Apple sign-ups
// go through a redirect and never reach it, so 75% of real accounts went uncounted
// and the goal recorded nothing for 28 days. The server knows when an account is new,
// whatever the method: the first-touch sync writes its record once per account, and
// checks the account is under a day old (infra/lambda/platform/signupInfo.js). So the
// conversion fires here, from the sync's answer, exactly once.
//
// Measurement never breaks what it measures: every call is wrapped, as in ctaGoal.ts.

import { trackEvent } from './plausible';
import { trackRedditEvent } from './reddit';
import { getAcquisition, type Acquisition } from './acquisition';
import { detectInAppBrowser } from '../utils/inAppBrowser';
import { deviceKind } from '../utils/device';

/** Must match the goal names in the Plausible dashboard exactly, including case. */
export const SIGNUP_GOAL = 'Signup';
export const SIGNUP_ERROR_GOAL = 'Signup Error';

export type SignupMethod = 'google' | 'apple' | 'email';

// Keyed by account. A per-device flag stopped every later account made in the same
// browser from ever syncing, so those sign-ups were never counted (#937). The old
// unkeyed flag is simply ignored: each existing account re-syncs once, the server's
// conditional write refuses it, and nothing fires.
const SYNCED_KEY_PREFIX = 'chaptery.acquisition.synced:';

// Set when the server says this sync emailed the account its download link (#938), so
// the empty bookshelf can say so. Keyed by account, and set only from that answer: the
// page must never claim an email that wasn't sent.
const HANDOFF_KEY_PREFIX = 'chaptery.handoffEmailSent:';

/** Dispatched on window when the flag is stored, so a page already on screen can update. */
export const HANDOFF_EMAIL_EVENT = 'chaptery:handoff-email-sent';

export interface HandoffEmailRecord {
  // When it was sent (ISO). Null for the old "1" flag, which was a sign-up send of unknown time.
  at: string | null;
  // Whether the address was verified: a verified one may be sent the link again after a
  // day, an unverified one never (the server's rule, handoffRequest.js).
  verified: boolean;
}

/** What this browser knows of the download link email to this account, or null. */
export function handoffEmailRecord(accountId: string | null | undefined): HandoffEmailRecord | null {
  if (!accountId) return null;
  try {
    const raw = window.localStorage.getItem(HANDOFF_KEY_PREFIX + accountId);
    if (raw === '1') return { at: null, verified: true };
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at?: unknown; verified?: unknown };
    if (typeof parsed?.verified !== 'boolean') return null;
    return { at: typeof parsed.at === 'string' ? parsed.at : null, verified: parsed.verified };
  } catch {
    return null;
  }
}

/** Whether this browser knows the download link was emailed to this account. */
export function handoffEmailWasSent(accountId: string | null | undefined): boolean {
  return handoffEmailRecord(accountId) !== null;
}

// The sign-up send only ever goes to a verified address, and has just happened, which are
// the defaults.
export function rememberHandoffEmail(accountId: string, verified = true, at: Date = new Date()): void {
  try {
    const record: HandoffEmailRecord = { at: at.toISOString(), verified };
    window.localStorage.setItem(HANDOFF_KEY_PREFIX + accountId, JSON.stringify(record));
    window.dispatchEvent(new Event(HANDOFF_EMAIL_EVENT));
  } catch {
    // Storage unavailable: the bookshelf keeps the line that makes no claim.
  }
}

/** The conversion, once per new account. Reddit reports only if its pixel loaded this page session. */
export function reportNewAccount(method: SignupMethod, acq: Acquisition | null): void {
  try {
    const props: Record<string, string> = { method };
    if (acq?.source) props.source = acq.source;
    if (acq?.medium) props.medium = acq.medium;
    if (acq?.campaign) props.campaign = acq.campaign;
    if (acq?.inApp) props.inApp = acq.inApp;
    // The platform name only, as with source; never the click ID. (#825)
    if (acq?.clickSource) props.clickSource = acq.clickSource;
    trackEvent(SIGNUP_GOAL, props);
    trackRedditEvent('SignUp');
  } catch {
    // Never let measurement break the thing being measured.
  }
}

/**
 * A sign-up that failed: the error's name only. Cognito's messages can contain the
 * address ("User x@y.com already exists"), and Plausible is a third party.
 */
export function trackSignupError(method: 'email' | 'redirect', error: unknown): void {
  try {
    const name = error && typeof error === 'object' && typeof (error as { name?: unknown }).name === 'string'
      ? (error as { name: string }).name
      : 'Unknown';
    trackEvent(SIGNUP_ERROR_GOAL, { method, error: name.slice(0, 80) });
  } catch {
    // Never let measurement break the thing being measured.
  }
}

function minimalFirstTouch(): Acquisition {
  return {
    firstSeen: new Date().toISOString(),
    landingPath: window.location.pathname,
    inApp: detectInAppBrowser()?.app,
    direct: true,
  };
}

const METHODS: SignupMethod[] = ['google', 'apple', 'email'];

/**
 * Attach the first touch to the account once per account and device, and report a new
 * account. Always syncs, even when nothing was stored: the old version skipped those,
 * which is one reason sign-ups went uncounted. Without an account id it does nothing.
 */
export async function syncFirstTouch(
  get: (path: string) => Promise<{ body: { json(): Promise<unknown> } }>,
  accountId: string | null | undefined,
): Promise<void> {
  if (!accountId) return;
  const syncedKey = SYNCED_KEY_PREFIX + accountId;
  try {
    if (window.localStorage.getItem(syncedKey)) return;
  } catch {
    // Storage unavailable: sync anyway. The server writes the record once, and the
    // conversion only fires when that write is the first.
  }
  const acq = getAcquisition() ?? minimalFirstTouch();
  // The device of this first signed-in load, almost always the one the account was made
  // on. Sent, not stored here: the server keeps it with the first touch, and emails the
  // download link when the account is new and the device can't run our installers (#938).
  const sent = { ...acq, device: deviceKind() };
  try {
    const res = await get('/profile?acq=' + encodeURIComponent(JSON.stringify(sent)));
    const body = (await res.body.json()) as {
      newAccount?: unknown; signupMethod?: unknown; firstTouchSaved?: unknown; handoffEmailSent?: unknown;
    };
    // The server could not store the record (a throttle, say): stay unsynced so the
    // next load tries again, rather than losing this account's first touch for good.
    if (body?.firstTouchSaved === false) return;
    try { window.localStorage.setItem(syncedKey, '1'); } catch { /* storage unavailable */ }
    if (body?.handoffEmailSent === true) rememberHandoffEmail(accountId);
    if (body?.newAccount === true) {
      const method = METHODS.includes(body.signupMethod as SignupMethod) ? body.signupMethod as SignupMethod : 'email';
      reportNewAccount(method, acq);
    }
  } catch {
    // Non-critical. The device stays unsynced, so the next load tries again.
  }
}
